import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Youtube, Image as ImageIcon, Search, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ytEmbedUrl, detectMediaType } from "@/lib/youtube";

const EMPTY = { name: "", muscle_group: "", media_url: "", media_urls: [], description: "" };

export default function ExercisesTab() {
  const [exercises, setExercises] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/exercises"); setExercises(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = exercises.filter(e => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (e.name || "").toLowerCase().includes(s) || (e.muscle_group || "").toLowerCase().includes(s);
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ name: e.name, muscle_group: e.muscle_group || "", media_url: e.media_url || "", media_urls: e.media_urls || [], description: e.description || "" }); setOpen(true); };

  const save = async (ev) => {
    ev.preventDefault();
    try {
      const payload = { ...form, media_type: detectMediaType(form.media_url) };
      if (editing) await api.patch(`/exercises/${editing.id}`, payload);
      else await api.post("/exercises", payload);
      toast.success("Esercizio salvato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Eliminare ${e.name}?`)) return;
    await api.delete(`/exercises/${e.id}`); load();
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/exercises/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      setForm(prev => ({ ...prev, media_urls: [...(prev.media_urls || []), ...uploaded] }));
      toast.success(`${uploaded.length} immagin${uploaded.length === 1 ? "e caricata" : "i caricate"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const removeUploadedImage = (idx) => {
    setForm(prev => ({ ...prev, media_urls: (prev.media_urls || []).filter((_, i) => i !== idx) }));
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="label-caps">Libreria esercizi</div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mt-1">{exercises.length} esercizi</h2>
        </div>
        <div className="flex gap-3 items-center flex-wrap w-full sm:w-auto">
          <div className="relative w-full sm:w-auto sm:flex-none min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca esercizio o gruppo"
              className="pl-9 pr-3 py-2 bg-transparent border border-white/15 focus:border-[#A855F7] outline-none text-sm text-white w-full sm:w-72" />
          </div>
          <button data-testid="add-exercise-btn" onClick={openCreate} className="btn-primary flex items-center gap-2 shrink-0">
            <Plus size={16} /> Nuovo esercizio
          </button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map(e => {
          const yt = ytEmbedUrl(e.media_url);
          const uploads = e.media_urls || [];
          const previewSrc = yt
            ? `https://img.youtube.com/vi/${yt.split("/").pop()}/mqdefault.jpg`
            : (uploads[0] ? absMedia(uploads[0]) : e.media_url || null);
          return (
            <div key={e.id} data-testid={`exercise-${e.id}`} className="card-surface p-4 flex gap-4">
              <div className="w-24 h-24 flex-shrink-0 border border-white/10 flex items-center justify-center overflow-hidden">
                {previewSrc ? (
                  <img src={previewSrc} alt="" className="w-full h-full object-cover" onError={ev => (ev.target.style.display = "none")} />
                ) : (
                  <ImageIcon size={20} className="text-white/30" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold uppercase tracking-tight text-base truncate">{e.name}</div>
                {e.muscle_group && <div className="text-[11px] uppercase tracking-widest text-[#A855F7] mt-0.5">{e.muscle_group}</div>}
                {e.description && <div className="text-xs text-white/50 mt-1 line-clamp-2">{e.description}</div>}
                <div className="flex items-center gap-2 mt-2">
                  {yt && <a href={e.media_url} target="_blank" rel="noreferrer" className="text-[10px] flex items-center gap-1 text-[#FF3366]"><Youtube size={12} /> YouTube</a>}
                  {uploads.length > 0 && <span className="text-[10px] flex items-center gap-1 text-white/50"><ImageIcon size={10} /> {uploads.length}</span>}
                  <button onClick={() => openEdit(e)} className="ml-auto p-1 text-white/50 hover:text-white"><Edit3 size={12} /></button>
                  <button onClick={() => remove(e)} className="p-1 text-[#FF3366]/70 hover:text-[#FF3366]"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="card-surface p-8 text-center text-white/40 col-span-full">Nessun esercizio. Aggiungine uno.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editing ? "Modifica" : "Nuovo"} esercizio</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-4">
            <Field label="Nome"><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="fld2" data-testid="ex-name" /></Field>
            <Field label="Gruppo muscolare"><input value={form.muscle_group} onChange={e => setForm({ ...form, muscle_group: e.target.value })} placeholder="Es. Petto, Gambe, Core..." className="fld2" data-testid="ex-mg" /></Field>
            <Field label="Link YouTube o URL immagine (opzionale)">
              <input value={form.media_url} onChange={e => setForm({ ...form, media_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=... o https://.../img.jpg" className="fld2" data-testid="ex-media" />
              {form.media_url && (
                <div className="mt-2 text-[11px] text-white/50">
                  Rilevato: {detectMediaType(form.media_url) === "youtube" ? "video YouTube" : "immagine"}
                </div>
              )}
            </Field>
            <Field label="Immagini caricate">
              <label className="border border-dashed border-white/25 hover:border-[#A855F7] p-3 flex items-center justify-center gap-2 cursor-pointer text-sm text-white/60 hover:text-white transition-colors" data-testid="ex-upload-label">
                <Upload size={14} />
                <span>{uploading ? "Caricamento…" : "Carica una o più immagini dal dispositivo"}</span>
                <input type="file" accept="image/*" multiple onChange={e => uploadFiles(e.target.files)} className="hidden" data-testid="ex-upload-input" disabled={uploading} />
              </label>
              {form.media_urls && form.media_urls.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid="ex-uploaded-grid">
                  {form.media_urls.map((u, i) => (
                    <div key={i} className="relative border border-white/10 aspect-square overflow-hidden group" data-testid={`ex-uploaded-${i}`}>
                      <img src={absMedia(u)} alt="" className="w-full h-full object-cover" />
                      <button type="button" data-testid={`ex-uploaded-remove-${i}`} onClick={() => removeUploadedImage(i)}
                        className="absolute top-1 right-1 bg-black/70 hover:bg-[#FF3366] w-6 h-6 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Note di esecuzione"><textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="fld2" data-testid="ex-desc" /></Field>
            <button type="submit" data-testid="ex-submit" className="btn-primary mt-2">{editing ? "Salva" : "Crea"}</button>
          </form>
          <style>{`.fld2{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.15);padding:10px 12px;color:#fff;outline:none;font-family:'Manrope',sans-serif;font-size:0.95rem}.fld2:focus{border-color:#A855F7}`}</style>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Resolve a relative /api/exercises/media/... URL to an absolute URL using REACT_APP_BACKEND_URL. */
function absMedia(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
}
export { absMedia };

function Field({ label, children }) { return <label className="block"><span className="label-caps block mb-2">{label}</span>{children}</label>; }
